// ============================================================================
// MarketRequestsPage — «Мои заявки»: карточки, статус, выбранный мастер,
// переход в карточку. Пусто/ошибка/загрузка — явные состояния.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { ClipboardList, Plus, RefreshCw, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OfflineDataBanner } from '@/components/ui/OfflineDataBanner';
import { useAuthStore } from '@/store/authStore';
import { eventBusHub } from '@/lib/eventBusHub';
import { logErrorToJournal } from '@/lib/errors';
import {
  isMarketPhoneBlocked,
  loadMyMarketRequests,
  toMarketFailure,
  type MarketFailure,
  type MarketRequest,
} from '@/lib/market';
import { MarketEmpty, MarketError, MarketLoading } from '@/components/market/MarketState';
import { MarketPhoneStatus } from '@/components/market/MarketPhoneStatus';
import { MarketRequestCard } from '@/components/market/MarketRequestCard';
import { MarketTopBar } from '@/components/market/MarketTopBar';
import { useI18n } from '@/lib/i18n/I18nProvider';

interface MarketRequestsPageProps {
  onBack: () => void;
  onCreate: () => void;
  onOpenRequest: (requestId: string) => void;
  onOpenProfile: () => void;
}

export function MarketRequestsPage({ onBack, onCreate, onOpenRequest, onOpenProfile }: MarketRequestsPageProps) {
  const userId = String(useAuthStore((s) => s.user?.id || ''));
  const { t } = useI18n();
  const [requests, setRequests] = useState<MarketRequest[] | null>(null);
  const [failure, setFailure] = useState<MarketFailure | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setFailure(null);
    try {
      const list = await loadMyMarketRequests(userId);
      setRequests(list);
    } catch (e) {
      logErrorToJournal(e, 'Витрина маркетплейса: список моих заявок');
      setFailure(toMarketFailure(e));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load, tick]);

  // Живое обновление: мастер взял заявку / изменился статус — перечитываем
  // список (событие публикует сервер, polling не нужен).
  useEffect(() => {
    return eventBusHub.subscribe((ev) => {
      if (ev.topic.startsWith('data.document.mkt_requests.')) setTick((n) => n + 1);
    });
  }, []);

  const phoneBlockedRequest = (requests || []).find(
    (request) => request.status === 'new' && isMarketPhoneBlocked(request),
  ) || null;

  return (
    <div className="flex h-full flex-col">
      <MarketTopBar
        title={t('market.my_requests')}
        subtitle={requests ? t('market.total', { count: requests.length }) : undefined}
        onBack={onBack}
        action={(
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 rounded-lg px-2 text-xs"
              onClick={() => setTick((n) => n + 1)}
              disabled={loading}
              aria-label={t('market.refresh')}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 rounded-lg p-0"
              onClick={onOpenProfile}
              aria-label={t('market.profile')}
              title={t('market.profile')}
            >
              <UserRound className="h-4 w-4" />
            </Button>
          </div>
        )}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-3 p-4 pb-24 sm:p-6">
          <OfflineDataBanner sourceKey={`market:requests:${userId}`} />

          <Button
            className="h-11 w-full rounded-xl"
            onClick={onCreate}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('market.leave_request')}
          </Button>

          {failure ? (
            <MarketError failure={failure} onRetry={() => setTick((n) => n + 1)} />
          ) : loading && requests === null ? (
            <MarketLoading text={t('market.requests_loading')} />
          ) : (requests?.length || 0) === 0 ? (
            <MarketEmpty
              icon={<ClipboardList className="h-5 w-5" />}
              title={t('market.requests_empty_title')}
              text={t('market.requests_empty_text')}
              action={<Button onClick={onCreate}>{t('market.leave_request')}</Button>}
            />
          ) : (
            <>
              {phoneBlockedRequest && (
                <MarketPhoneStatus
                  state="unverified"
                  audience="client"
                  hint={phoneBlockedRequest.phoneVerificationHint || undefined}
                  onOpenProfile={onOpenProfile}
                />
              )}
              <div className="space-y-2.5">
                {requests!.map((request) => (
                  <MarketRequestCard
                    key={request.id}
                    request={request}
                    onClick={() => onOpenRequest(request.id)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
